import clsx from "clsx";
import { Loader2, Upload } from "lucide-react";
import { FC, useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { FormattedMessage, useIntl } from "react-intl";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { Progress } from "~/components/ui/progress";
import { MAX_FILE_SIZE_BYTES } from "~/features/settings/components/reports/importExportUtils";
import { useToast } from "~/hooks/use-toast";

const ACCEPTED_REPORT_TYPES = {
  "application/pdf": [".pdf"],
  "text/markdown": [".md", ".markdown"],
};

interface FtueUploadReportProps {
  isUploading: boolean;
  uploadProgress: number;
  onUpload: (file: File) => Promise<void>;
  onSkip: () => void;
}

export const FtueUploadReport: FC<FtueUploadReportProps> = ({
  isUploading,
  uploadProgress,
  onUpload,
  onSkip,
}) => {
  const intl = useIntl();
  const { toast } = useToast();
  const [isDragActive, setIsDragActive] = useState(false);

  const handleDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (!file) return;

      if (file.size > MAX_FILE_SIZE_BYTES) {
        toast({
          variant: "destructive",
          title: intl.formatMessage({
            id: "importTemplate.fileTooLarge",
            defaultMessage: "File size exceeds the maximum limit of 25MB",
          }),
        });
        return;
      }

      await onUpload(file);
    },
    [intl, onUpload, toast]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDragEnter: () => setIsDragActive(true),
    onDragLeave: () => setIsDragActive(false),
    onDrop: (acceptedFiles, fileRejections) => {
      setIsDragActive(false);
      if (fileRejections.length > 0) {
        toast({
          variant: "destructive",
          title: intl.formatMessage({
            id: "ftue.uploadReport.invalidFile",
            defaultMessage: "Only PDF and Markdown files are allowed",
          }),
        });
        return;
      }
      handleDrop(acceptedFiles);
    },
    accept: ACCEPTED_REPORT_TYPES,
    multiple: false,
    disabled: isUploading,
  });

  return (
    <div className="flex flex-col items-center justify-center min-h-[calc(100vh-200px)] px-4">
      <div className="max-w-xl w-full text-center space-y-8">
        <div className="space-y-3">
          <h1 className="text-3xl font-semibold text-foreground">
            <FormattedMessage
              id="ftue.uploadReport.title"
              defaultMessage="Upload a Sample Report"
            />
          </h1>
          <p className="text-lg text-muted-foreground">
            <FormattedMessage
              id="ftue.uploadReport.subtitle"
              defaultMessage="Upload an example of a finished report — the kind you send to your clients. Your formatting, structure, and tone will be preserved in every future report, automatically."
            />
          </p>
        </div>

        <Card
          {...getRootProps()}
          className={clsx("cursor-pointer transition-all", {
            "border-blue-300 bg-blue-50/50 dark:border-blue-700 dark:bg-blue-950/50":
              isUploading,
            "border-blue-500 border-dashed shadow-md bg-blue-50/30 dark:bg-blue-950/30":
              isDragActive && !isUploading,
            "hover:border-blue-500 hover:shadow-md":
              !isUploading && !isDragActive,
          })}
        >
          <input {...getInputProps()} />
          <CardHeader className="text-center py-10">
            {isUploading ? (
              <>
                <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                  <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
                </div>
                <CardTitle className="text-lg text-blue-700 dark:text-blue-300">
                  <FormattedMessage
                    id="ftue.uploadReport.uploading"
                    defaultMessage="Uploading your report..."
                  />
                </CardTitle>
                <div className="mt-4 px-8">
                  <Progress value={uploadProgress} className="h-2" />
                  <p className="text-sm text-muted-foreground mt-2">
                    {uploadProgress}%
                  </p>
                </div>
              </>
            ) : (
              <>
                <div
                  className={clsx(
                    "mx-auto mb-4 w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center",
                    { "opacity-50": isDragActive }
                  )}
                >
                  <Upload className="w-8 h-8 text-blue-600" />
                </div>
                <CardTitle className="text-lg">
                  <FormattedMessage
                    id="ftue.uploadReport.dropzone"
                    defaultMessage="Click to upload your report"
                  />
                </CardTitle>
                <CardDescription className="mt-2">
                  <FormattedMessage
                    id="ftue.uploadReport.formats"
                    defaultMessage="Supports PDF file (max 25MB)"
                  />
                </CardDescription>
              </>
            )}
          </CardHeader>
        </Card>

        <div className="flex justify-center gap-4">
          <Button
            variant="link"
            onClick={onSkip}
            disabled={isUploading}
            className="text-muted-foreground hover:text-foreground"
          >
            <FormattedMessage
              id="ftue.uploadReport.skip"
              defaultMessage="Skip for Now"
            />
          </Button>
        </div>
      </div>
    </div>
  );
};
